'use client'

/**
 * GoogleSearchesBoard — the stocks America is Googling right now, resolved from Google's "Trending Now"
 * (Business & Finance) search data to our tickers and ranked by approximate search traffic. A window
 * toggle (4h / 24h / 48h / 7d) switches between pre-loaded windows (server-fetched, Redis-cached), so
 * toggling is instant. Each ticker links to its hub. Search intent, not a signal — do your own research.
 */

import { useState } from 'react'
import Link from 'next/link'
import { Search, ExternalLink } from 'lucide-react'
import type { TrendingStock } from '@/lib/dashboard/googleTrendingRpc'

export type GoogleWindow = { hours: number; label: string; stocks: TrendingStock[] }

const fmtTraffic = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}K+` : n > 0 ? `${n}+` : '')

export default function GoogleSearchesBoard({ windows, defaultHours = 24 }: { windows: GoogleWindow[]; defaultHours?: number }) {
  const [hours, setHours] = useState(defaultHours)
  const active = windows.find((w) => w.hours === hours) ?? windows[0]
  const stocks = active?.stocks ?? []

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-600">Window</span>
        {windows.map((w) => (
          <button
            key={w.hours}
            onClick={() => setHours(w.hours)}
            title={`Stocks trending in Google searches over the last ${w.label}`}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${hours === w.hours ? 'border-sky-500/60 bg-sky-500/15 text-sky-200' : 'border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200'}`}
          >
            {w.label}
          </button>
        ))}
      </div>

      {stocks.length ? (
        <ol className="space-y-1.5">
          {stocks.map((s, i) => (
            <li key={s.symbol} className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-3 transition-colors hover:border-zinc-700/80">
              <div className="flex items-center gap-3">
                <span className="w-5 shrink-0 text-right text-[13px] font-bold tabular-nums text-zinc-600">{i + 1}</span>
                <Link href={`/stocks/${encodeURIComponent(s.symbol)}`} className="shrink-0 rounded-lg bg-sky-500/10 px-2 py-0.5 text-sm font-extrabold text-sky-300 hover:bg-sky-500/20">${s.symbol}</Link>
                <span className="min-w-0 flex-1 truncate text-[13px] text-zinc-400" title={s.term}>
                  <Search className="mr-1 inline h-3 w-3 text-zinc-600" />
                  {s.term}
                </span>
                {fmtTraffic(s.traffic) && (
                  <span className="shrink-0 rounded-md bg-zinc-800/70 px-2 py-0.5 text-[11px] font-bold tabular-nums text-zinc-300" title="Approximate Google searches in this window">{fmtTraffic(s.traffic)}</span>
                )}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/30 px-4 py-10 text-center text-sm text-zinc-500">
          No stock-linked finance searches trending in this window right now. Try a wider window, or check back shortly.
        </div>
      )}

      <p className="mt-2 text-[11px] leading-snug text-zinc-600">
        <ExternalLink className="mr-0.5 inline h-3 w-3" />
        Source: Google &ldquo;Trending Now&rdquo; (Business &amp; Finance), resolved to tickers. Search interest, not a buy signal.
      </p>
    </div>
  )
}
